import { PROMOCODE_CONFIG, PromocodeHelpers } from '../config/promocode.js';

class PromocodeDecoder {
  /**
   * Decode a promocode to extract platform and plan details
   * @param {string} promocode - The promocode to decode
   * @returns {object} Decoded promocode information
   */
  static decode(promocode) {
    if (!promocode || typeof promocode !== 'string') {
      return { isValid: false, error: 'Invalid promocode format' };
    }

    // Try structured format first
    const structuredMatch = promocode.match(PROMOCODE_CONFIG.patterns.structured);
    if (structuredMatch) {
      return this.parseStructuredPromocode(structuredMatch, promocode);
    }

    // Try legacy format
    const legacyMatch = promocode.match(PROMOCODE_CONFIG.patterns.legacy);
    if (legacyMatch) {
      return this.parseLegacyPromocode(promocode);
    }

    // Invalid format
    return { isValid: false, error: 'Invalid promocode format' };
  }

  /**
   * Parse structured promocode format: [PLATFORM][PLAN][CREDITS][EXPIRY][RANDOM]
   * Example: ASL140K2025ABC12345
   */
  static parseStructuredPromocode(match, originalCode) {
    const [, platformCode, planCode, creditsStr, expiryYear, randomSuffix] = match;
    
    // Ensure platforms are loaded before lookup
    PromocodeHelpers.ensurePlatformsLoaded();
    const platform = PromocodeHelpers.getPlatformByCode(platformCode);
    if (!platform) {
      // For unknown platforms, create a generic platform entry
      return {
        isValid: true,
        platform: 'unknown',
        platformCode: platformCode,
        planType: this.decodePlanType(planCode),
        credits: this.decodeCredits(creditsStr),
        expiryYear: parseInt(expiryYear),
        randomSuffix: randomSuffix,
        originalCode: originalCode,
        isStructured: true,
        isUnknownPlatform: true
      };
    }

    const planType = this.decodePlanType(planCode);
    const credits = this.decodeCredits(creditsStr);
    const expiryYearNum = parseInt(expiryYear);

    return {
      isValid: true,
      platform: platform.name.toLowerCase(),
      platformCode: platformCode,
      planType: planType,
      credits: credits,
      expiryYear: expiryYearNum,
      randomSuffix: randomSuffix,
      originalCode: originalCode,
      isStructured: true
    };
  }

  /**
   * Parse legacy promocode (backward compatibility)
   */
  static parseLegacyPromocode(promocode) {
    // For existing codes, assume default values based on common patterns
    // This maintains backward compatibility without hardcoding specific platforms
    return {
      isValid: true,
      platform: 'legacy', // Generic legacy platform
      platformCode: 'LG', // Legacy platform code
      planType: 'lifetime',
      credits: 10000, // Default credits for legacy codes
      expiryYear: new Date().getFullYear() + 1,
      randomSuffix: promocode.slice(-8),
      originalCode: promocode,
      isLegacy: true
    };
  }

  /**
   * Decode plan type from single character code
   */
  static decodePlanType(code) {
    const planTypes = {
      'L': 'lifetime',
      'M': 'monthly',
      'Y': 'yearly'
    };
    return planTypes[code] || 'lifetime';
  }

  /**
   * Decode credits from string with K/M suffix
   */
  static decodeCredits(creditsStr) {
    if (creditsStr.endsWith('K')) {
      return parseInt(creditsStr.slice(0, -1)) * 1000;
    } else if (creditsStr.endsWith('M')) {
      return parseInt(creditsStr.slice(0, -1)) * 1000000;
    }
    return parseInt(creditsStr);
  }

  /**
   * Get credit system plan details for a decoded promocode
   */
  static getCreditSystemDetails(decodedPromocode) {
    const planConfig = PromocodeHelpers.getPlanTypeConfig(decodedPromocode.planType);
    
    return {
      subscriptionPlan: PromocodeHelpers.getCreditSystemPlan(decodedPromocode.platform, decodedPromocode.planType),
      billingCycle: planConfig.billingCycle,
      credits: decodedPromocode.credits,
      planType: decodedPromocode.planType,
      platform: decodedPromocode.platform,
      resetExtensionYears: planConfig.resetExtensionYears,
      resetExtensionMonths: planConfig.resetExtensionMonths
    };
  }

  /**
   * Validate if a promocode is still valid (not expired)
   */
  static isPromocodeValid(decodedPromocode) {
    if (!decodedPromocode.isValid) {
      return false;
    }

    const currentYear = new Date().getFullYear();
    return decodedPromocode.expiryYear >= currentYear;
  }
}

export default PromocodeDecoder;
